/**
 * Platform favicons — small inline SVG icons keyed by platform slug.
 *
 * Each icon is brand-coloured, square, and renders crisp at 12-32px.
 * Used in the marketing calendar to identify the platform of each
 * scheduled activity at a glance, and reusable anywhere we list
 * channel-tagged content.
 */
import React from "react";

export const PLATFORMS = [
  { key: "facebook",        label: "Facebook",          color: "#1877F2" },
  { key: "instagram",       label: "Instagram",         color: "#E1306C" },
  { key: "x",               label: "X (Twitter)",       color: "#000000" },
  { key: "linkedin",        label: "LinkedIn",          color: "#0A66C2" },
  { key: "tiktok",          label: "TikTok",            color: "#010101" },
  { key: "youtube",         label: "YouTube",           color: "#FF0000" },
  { key: "pinterest",       label: "Pinterest",         color: "#E60023" },
  { key: "threads",         label: "Threads",           color: "#000000" },
  { key: "google_business", label: "Google Business",   color: "#4285F4" },
  { key: "wordpress",       label: "WordPress",         color: "#21759B" },
  { key: "blog",            label: "Blog post",         color: "#52525B" },
  { key: "email",           label: "Email / Newsletter", color: "#1A82E2" },
  { key: "sms",             label: "SMS",               color: "#0EA371" },
  { key: "generic",         label: "Other / Generic",   color: "#71717A" },
];

const ICON_PATHS = {
  facebook: (
    <>
      <rect width="24" height="24" rx="4" fill="#1877F2" />
      <path
        d="M15.5 8h-1.6c-.4 0-.6.3-.6.7v1.6h2.2l-.3 2.3h-1.9V19h-2.4v-6.4H9V10.3h1.9V8.6c0-1.6 1-2.6 2.6-2.6h2v2z"
        fill="#fff"
      />
    </>
  ),
  instagram: (
    <>
      <defs>
        <linearGradient id="ig-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FEDA75" />
          <stop offset="35%" stopColor="#FA7E1E" />
          <stop offset="60%" stopColor="#D62976" />
          <stop offset="100%" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#ig-grad)" />
      <rect x="6" y="6" width="12" height="12" rx="4"
        fill="none" stroke="#fff" strokeWidth="1.6" />
      <circle cx="12" cy="12" r="2.8" fill="none" stroke="#fff" strokeWidth="1.6" />
      <circle cx="16" cy="8" r=".9" fill="#fff" />
    </>
  ),
  x: (
    <>
      <rect width="24" height="24" rx="4" fill="#000" />
      <path
        d="M16.6 6h2.3l-5 5.7L20 18h-4.6l-3.6-4.4L7.5 18H5.2l5.3-6.1L5 6h4.7l3.3 4.1L16.6 6zm-.8 10.6h1.3L8.3 7.3H7l8.8 9.3z"
        fill="#fff"
      />
    </>
  ),
  linkedin: (
    <>
      <rect width="24" height="24" rx="4" fill="#0A66C2" />
      <circle cx="7.5" cy="7.5" r="1.5" fill="#fff" />
      <path d="M6 10.5h3v8H6v-8z" fill="#fff" />
      <path
        d="M10.5 10.5h2.9v1.1h.04c.4-.7 1.4-1.4 2.86-1.4 3 0 3.6 2 3.6 4.5v3.8h-3v-3.4c0-.8 0-1.9-1.2-1.9s-1.4.9-1.4 1.8v3.5h-3v-8z"
        fill="#fff"
      />
    </>
  ),
  tiktok: (
    <>
      <rect width="24" height="24" rx="4" fill="#000" />
      <path
        d="M16.6 8.7a4 4 0 01-2.4-.9V14a4.5 4.5 0 11-4.5-4.5c.2 0 .4 0 .6.05v2.3a2.2 2.2 0 102.2 2.2V4.5h2.2A4 4 0 0017 8.7v.07h-.4z"
        fill="#25F4EE"
      />
      <path
        d="M16.2 8.4a4 4 0 01-2.4-.9V13a4.5 4.5 0 11-4.5-4.5c.2 0 .4 0 .6.05v2.3a2.2 2.2 0 102.2 2.2V4.2h2.2a4 4 0 002.3 3.7v.07l-.4.4z"
        fill="#FE2C55"
      />
      <path
        d="M16 8.1a4 4 0 01-2.4-.9V12.7a4.5 4.5 0 11-4.5-4.5c.2 0 .4 0 .6.05v2.3a2.2 2.2 0 102.2 2.2V4h2.2A4 4 0 0016.4 7.7v.4H16z"
        fill="#fff"
      />
    </>
  ),
  youtube: (
    <>
      <rect width="24" height="24" rx="4" fill="#FF0000" />
      <path d="M10 9v6l5.2-3L10 9z" fill="#fff" />
    </>
  ),
  pinterest: (
    <>
      <rect width="24" height="24" rx="12" fill="#E60023" />
      <path
        d="M12 4.5a7.5 7.5 0 00-2.8 14.5c-.05-.5-.1-1.4 0-2L10.4 13s-.3-.6-.3-1.5c0-1.4.85-2.4 1.9-2.4.9 0 1.3.65 1.3 1.45 0 .9-.55 2.2-.85 3.4-.25.95.5 1.7 1.45 1.7 1.7 0 3-1.8 3-4.4 0-2.3-1.65-3.9-4-3.9-2.7 0-4.3 2-4.3 4.2 0 .8.3 1.6.7 2.1.08.1.1.18.07.3l-.3 1.1c-.05.18-.15.25-.35.15-1.2-.55-1.95-2.3-1.95-3.7 0-3 2.2-5.8 6.3-5.8 3.3 0 5.85 2.4 5.85 5.5 0 3.3-2.05 5.95-4.9 5.95-.95 0-1.85-.5-2.15-1.1l-.6 2.2c-.2.8-.75 1.8-1.15 2.4A7.5 7.5 0 1012 4.5z"
        fill="#fff"
      />
    </>
  ),
  threads: (
    <>
      <rect width="24" height="24" rx="6" fill="#000" />
      <path
        d="M12.2 5c-3.6 0-6.2 2.4-6.2 6.5 0 4.5 2.7 7 6 7 2.1 0 3.55-.7 4.55-2.2.7-1.05.85-2.4.3-3.4-.6-1.05-1.85-1.55-3.3-1.55-1 0-2.2.35-2.5 1.55-.2.85.35 1.65 1.3 1.85.7.15 1.3-.15 1.5-.7.06-.2.5.6.5 1 0 .9-.95 1.55-2.1 1.55-2.3 0-3.65-1.9-3.65-4.1 0-3.2 2-4.8 4.5-4.8 2.05 0 3.7 1 4.65 2.55l1.45-.9C17.1 6.65 14.95 5 12.2 5z"
        fill="#fff"
      />
    </>
  ),
  google_business: (
    <>
      <rect width="24" height="24" rx="4" fill="#fff" stroke="#E4E4E7" />
      <path d="M5 9h3v6H5z" fill="#4285F4" />
      <path d="M8 6h3v9H8z" fill="#34A853" />
      <path d="M11 8h3v7h-3z" fill="#FBBC04" />
      <path d="M14 10h5v5h-5z" fill="#EA4335" />
    </>
  ),
  wordpress: (
    <>
      <circle cx="12" cy="12" r="10" fill="#21759B" />
      <path
        d="M4.5 12a7.5 7.5 0 003.9 6.6L5 8.4A7.4 7.4 0 004.5 12zm12.4-.4c0-.95-.35-1.6-.65-2.1-.4-.65-.75-1.2-.75-1.85 0-.7.55-1.4 1.3-1.4l.1.01a7.5 7.5 0 00-11.3 1.4l.5.01c.85 0 2.15-.1 2.15-.1.4-.02.5.6.05.65 0 0-.45.05-.95.1l2.95 8.75 1.75-5.3-1.25-3.4c-.45-.05-.85-.1-.85-.1-.45-.05-.4-.7.05-.65 0 0 1.3.1 2.1.1.85 0 2.15-.1 2.15-.1.45-.02.5.6.05.65 0 0-.45.05-.95.1l2.95 8.75.8-2.7.5-1.8zm-4.6 1l-2.45 7.1a7.5 7.5 0 004.6-.1.74.74 0 01-.05-.1l-2.1-7zm6.25-4.1c.04.25.05.55.05.85 0 .85-.15 1.8-.65 3l-2.6 7.5a7.5 7.5 0 003.2-11.35z"
        fill="#fff"
      />
    </>
  ),
  blog: (
    <>
      <rect width="24" height="24" rx="4" fill="#52525B" />
      <path d="M7 7h10v1.5H7zM7 10.5h10V12H7zM7 14h7v1.5H7z" fill="#fff" />
    </>
  ),
  email: (
    <>
      <rect width="24" height="24" rx="4" fill="#1A82E2" />
      <path
        d="M5 8.5l7 4.5 7-4.5V8a1 1 0 00-1-1H6a1 1 0 00-1 1v.5zm0 1.65V16a1 1 0 001 1h12a1 1 0 001-1v-5.85l-6.45 4.15a1 1 0 01-1.1 0L5 10.15z"
        fill="#fff"
      />
    </>
  ),
  sms: (
    <>
      <rect width="24" height="24" rx="4" fill="#0EA371" />
      <path
        d="M5 7a2 2 0 012-2h10a2 2 0 012 2v6a2 2 0 01-2 2H10l-3.5 3v-3H7a2 2 0 01-2-2V7zm3.5 2v1.5h2V9h-2zm3 0v1.5h2V9h-2zm3 0v1.5h2V9h-2z"
        fill="#fff"
      />
    </>
  ),
  generic: (
    <>
      <rect width="24" height="24" rx="4" fill="#71717A" />
      <circle cx="12" cy="12" r="3.5" fill="#fff" />
    </>
  ),
};

export function PlatformIcon({ platform = "generic", size = 16, className = "", title }) {
  const key = (platform || "generic").toLowerCase();
  const inner = ICON_PATHS[key] || ICON_PATHS.generic;
  const meta = PLATFORMS.find((p) => p.key === key);
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      aria-label={title || meta?.label || key}
      role="img"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <title>{title || meta?.label || key}</title>
      {inner}
    </svg>
  );
}
